import React, {useEffect, useRef} from 'react';
import {observer} from 'mobx-react';
import {SingleGameComponent} from '../single-game/single-game.component';
import {SingleGamePlaceholderComponent} from '../single-game-placeholder/single-game-placeholder.component';
import classNames from 'classnames';
import {SwiperWrapperBox} from './boxes/swiper-wrapper/swiper-wrapper.box';
import {SwiperGamesListBox} from './boxes/swiper-games-list/swiper-games-list.box';
import {SwiperBox} from './boxes/swiper/swiper.box';
import {SingleGameSkeletonComponent} from '../single-game/single-game-skeleton.component';
import {CyClass} from '../../../common/cy-classes';
import {ISwiperListViewModel} from "../../services/arenas/models/lists/swiper-list-view-model.interface";
import {useServices} from "../../react-hooks/use-services.hook";

export interface OrbitSwiperComponentProps {
    viewModel: ISwiperListViewModel;
    numberOfPlaceholders?: number;
    skipIntermediatePage?: boolean;
    showNumberBadge?: boolean;
    className?: string;
    listPosition: number;
    lastGameType?: number;
    onGameBringInCentralView?: (gameIndex: number) => void;
}

export const OrbitSwiperComponent: React.FC<OrbitSwiperComponentProps> = observer((props) => {
    const services = useServices();
    const _swiperGameListElementRef = useRef<HTMLDivElement | null>(null);
    const _swiperWrapperElementRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        props.viewModel.swipeLogicHandler.setElement(_swiperGameListElementRef.current);

        return () => {
            props.viewModel.swipeLogicHandler.setElement(null);
        }

    }, [props.viewModel.swipeLogicHandler]);


    const _shouldUseSquareIcons = () => {
        return props.viewModel.shouldUseSquareIcons();
    }

    const _renderOneGame = (gameType: number, gameIndex: number, isLastGameType?: boolean) => {
        return <SingleGameComponent
            key={gameType}
            gameType={gameType}
            seoCategory={props.viewModel.arenaName}
            disableGameInfoHover={false}
            listTitle={props.viewModel.title.translationKey}
            listPosition={props.listPosition}
            AdminListID={props.viewModel.listId}
            AIListTypeID={props.viewModel.listTypeId}
            skipIntermediatePage={props.skipIntermediatePage}
            templateType={props.viewModel.templateType}
            intersectionTarget={_swiperWrapperElementRef.current}
            gameIndex={gameIndex}
            showNumberBadge={props.showNumberBadge}
            isInsideSwiper={true}
            useSquareIcons={_shouldUseSquareIcons()}
            aiEngineName={props.viewModel.aiEngine}
            hasTwoLinesTitle={props.viewModel.showGameTitleOnMultipleLines}
            isLastGametype={isLastGameType}
            onBringInCentralView={props.onGameBringInCentralView}
        />;
    }

    const _renderPlaceholders = () => {

        if (props.viewModel.numberOfGamesSkeletons > 0) {
            return null;
        }

        let placeHolders: React.ReactNode[] = [];
        let numberOfPlaceholders = props.numberOfPlaceholders || 0;

        for (let i = 0; i < numberOfPlaceholders; i++) {
            placeHolders.push(<SingleGamePlaceholderComponent key={'game_placeholder_' + i} isInsideSwiper={true}/>);
        }

        return placeHolders;
    }

    const _renderGamesSkeletons = () => {

        let numSkeletons = props.viewModel.numberOfGamesSkeletons;

        let skeletons = [];

        for (let i = 1; i <= numSkeletons; i++) {
            skeletons.push(
                <SingleGameSkeletonComponent
                    key={'skeleton_' + i}
                    templateType={props.viewModel.templateType}
                    useSquareIcons={_shouldUseSquareIcons()}
                />
            );
        }

        return skeletons;
    }

    const _renderGames = () => {

        const gameTypes = props.viewModel.gameTypes;
        if (gameTypes.length > 0) {
            return gameTypes.map((gameType, index) => {
                return _renderOneGame(gameType, index, gameType === props.lastGameType)
            });
        }
        return _renderGamesSkeletons();
    }


    if (!props.viewModel.isVisible) {
        return null;
    }

    return (
        <SwiperBox className={classNames(CyClass('cy-orbit-swiper'), props.className)}>

            {props.viewModel.renderListHeader()}

            <SwiperWrapperBox className={classNames('cy-orbit-swiper-wrapper', props.viewModel.templateType)}
                              ref={_swiperWrapperElementRef}>
                <SwiperGamesListBox className='cy-orbit-swiper-games-list' ref={_swiperGameListElementRef}
                                    isTouchDevice={services.device.feature.isTouch}>
                    {props.viewModel.renderSpecialWidgets()}
                    {_renderGames()}
                    {_renderPlaceholders()}
                </SwiperGamesListBox>
                {props.viewModel.renderListNavigation()}
            </SwiperWrapperBox>

            {props.viewModel.renderListFooter()}

        </SwiperBox>
    );

});

